import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  HiOutlineArrowSmUp,
  HiOutlineCash,
  HiOutlineCube,
  HiOutlineUsers,
  HiOutlineChartBar,
  HiOutlineChartSquareBar,
  HiOutlineScale,
  HiOutlineDocumentText,
} from "react-icons/hi";

import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";
import BalancePorPeriodo from "./BalancePorPeriodo";
import SectionHeader from "../../Elements/SectionHeader";
import './ReportsPage.css';
import FlujoContable from "./FlujoContable";
import ControlDeStock from "./ControlDeStock";
import SociosStats from "./SociosStats";
import CajasStats from "./CajasStats";
import MermaReport from "./MermaReport";
import ContabilidadGeneral from "./ContabilidadGeneral";

const MAIN_TABS = [
  { id: "balance", label: "Balance por Período", icon: <HiOutlineChartBar size={16} /> },
  { id: "flujo", label: "Flujo Contable", icon: <HiOutlineChartSquareBar size={16} /> },
  { id: "stock", label: "Control de Stock", icon: <HiOutlineCube size={16} /> },
  { id: "socios", label: "Socios", icon: <HiOutlineUsers size={16} /> },
  { id: "caja", label: "Cajas", icon: <HiOutlineCash size={16} /> },
  { id: "merma", label: "Merma", icon: <HiOutlineScale size={16} /> },
  { id: "contabilidad", label: "Contabilidad", icon: <HiOutlineDocumentText size={16} /> },
];

const DAYS = 30;

function formatCurrency(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatCompact(value) {
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(Number(value || 0));
}

// Tarjeta KPI del sistema: sin sombra, radio 8, etiqueta en sentence case y el
// icono en el primario (antes cuatro colores —esmeralda, cian, ámbar, rosa— que
// no significaban nada: no eran una escala, eran adorno). Auditoría de UI, UI-006.
function StatCard({ label, value, hint, icon }) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs text-[var(--muted-foreground)]">{label}</div>
          <div className="mt-2 text-2xl font-semibold text-[var(--foreground)]">{value}</div>
          <div className="mt-1 text-xs text-[var(--muted-foreground)]">{hint}</div>
        </div>
        <div className="shrink-0 rounded-md bg-[var(--muted)] p-2.5 text-[var(--primary)]">{icon}</div>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [token, , , venueID, venueName] = useContext(UserContext);
  const [activeTab, setActiveTab] = useState("balance");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) { setLoading(false); return; }
      setLoading(true);
      setError("");
      const q = new URLSearchParams({ days: String(DAYS) });
      if (venueID) q.set("venue_id", venueID);
      try {
        const res = await apiFetch(`/v1/reports/overview?${q}`, { headers: authHeaders });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        if (!cancelled) setOverview(await res.json());
      } catch (e) {
        if (!cancelled) setError(e.message || "Error cargando informes");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [authHeaders, token, venueID]);

  const overviewCards = useMemo(() => {
    if (!overview) return [];
    // Desglose por método de pago (para cuadrar caja: solo el efectivo entra en ella).
    const salesM = overview.sales_by_method;
    const depsM = overview.deposits_by_method;
    const salesHint = (
      <>
        <div>{formatCompact(overview.total_orders)} ops en {overview.period_days}d</div>
        {salesM && (
          <div>Efectivo {formatCurrency(salesM.cash)} · Tarjeta {formatCurrency(salesM.card)} · Saldo {formatCurrency(salesM.credit)}</div>
        )}
      </>
    );
    const depositsHint = (
      <>
        {depsM && (
          <div>Efectivo {formatCurrency(depsM.cash)} · Tarjeta {formatCurrency(depsM.card)}</div>
        )}
        <div>{formatCurrency(overview.net_cash_flow)} flujo neto</div>
      </>
    );
    return [
      { label: "Ventas",       value: formatCurrency(overview.total_sales),    hint: salesHint,    icon: <HiOutlineArrowSmUp size={22} /> },
      { label: "Depósitos",    value: formatCurrency(overview.total_deposits), hint: depositsHint, icon: <HiOutlineCash size={22} /> },
      { label: "Socios",       value: formatCompact(overview.total_members),   hint: `${formatCompact(overview.new_members)} altas nuevas`,                     icon: <HiOutlineUsers size={22} /> },
      { label: "Stock crítico",value: formatCompact(overview.low_stock_count), hint: `${formatCompact(overview.open_cash_boxes)} cajas abiertas`,               icon: <HiOutlineCube size={22} /> },
    ];
  }, [overview]);

  function renderTab() {
    if (activeTab === "balance") return <BalancePorPeriodo token={token} venueID={venueID} />;
    if (activeTab === "flujo")   return <FlujoContable token={token} venueID={venueID} />;
    if (activeTab === "stock")   return <ControlDeStock token={token} venueID={venueID} />;
    if (activeTab === "socios")  return <SociosStats token={token} venueID={venueID} />;
    if (activeTab === "caja")    return <CajasStats token={token} venueID={venueID} />;
    if (activeTab === "merma")   return <MermaReport token={token} venueID={venueID} />;
    if (activeTab === "contabilidad") return <ContabilidadGeneral token={token} venueID={venueID} />;
    return null;
  }

  return (
    <div className="flex flex-col gap-4 p-0 sm:p-2 md:p-4">
      {/* La cabecera del sistema, como en Actividad: el hero con degradado a
          lima (radio 28, sombra, versalitas) era la única sección del producto
          que no se parecía a ninguna otra (auditoría de UI, UI-006). */}
      <SectionHeader
        title="Panel analítico"
        subtitle={`Balance, flujo contable, stock y socios — datos en tiempo real.${venueName ? ` · ${venueName}` : ""}`}
        sticky={false}
      />

      {/* Overview stat cards */}
      {error && (
        <div className="rounded-md border border-[var(--destructive-border)] bg-[var(--destructive-bg)] px-4 py-3 text-sm text-[var(--destructive)]">{error}</div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {(loading ? [
          { label: "Ventas", value: "…", hint: "", icon: <HiOutlineArrowSmUp size={22} /> },
          { label: "Depósitos", value: "…", hint: "", icon: <HiOutlineCash size={22} /> },
          { label: "Socios", value: "…", hint: "", icon: <HiOutlineUsers size={22} /> },
          { label: "Stock crítico", value: "…", hint: "", icon: <HiOutlineCube size={22} /> },
        ] : overviewCards).map((card) => (
          <StatCard
            key={card.label}
            label={card.label}
            value={card.value}
            hint={card.hint}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Main tab navigation */}
      {/* Pestañas: el mismo patrón que las categorías del Dispensario de Pablo
          (.df-cat): fondo muted, la activa en tarjeta blanca con borde. Nada de
          lima, nada de sombra. */}
      <div className="wa-reportes-tabs-row wa-reportes-main-tabs bg-[var(--muted)] rounded-md p-1 flex flex-wrap gap-1">
        {MAIN_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            aria-pressed={activeTab === tab.id}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-sm whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? "bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)]"
                : "border border-transparent text-[var(--muted-foreground)] hover:bg-[var(--secondary)] hover:text-[var(--foreground)]"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>{renderTab()}</div>
    </div>
  );
}
